import { useSelector } from "react-redux";

import { ROLES } from "../helpers/constants";
import { decryptPayload } from "../helpers/utils";

export const COUNTY_RECORDER_MENU = [
  { label: "Dashboard", to: "/admin-dashboard" },
  { label: "Users", to: "/admin/users" },
  { label: "Risk Assessments", to: "/admin/risk-assessments" },
  { label: "Customer Service Queue", to: "/customer-service-queue" },
  { label: "EP Outreach", to: "/admin/outreach" },
];

export function storedRoleList(roles) {
  if (Array.isArray(roles)) {
    return roles.filter(Boolean);
  }
  if (roles) {
    return [roles];
  }
  return [];
}

export function isCountyRecorderRole(roles) {
  return storedRoleList(roles).includes(ROLES.countyRecorder);
}

export function isCountyRecorderSession() {
  const roles = decryptPayload(localStorage.getItem("roles"));
  return isCountyRecorderRole(Array.isArray(roles) ? roles : [roles]);
}

export function isCarPartnerSession() {
  return localStorage.getItem("adminPartnerScope") === "car";
}

export const CAR_DASHBOARD_MENU_ITEM = { label: "C.A.R. Dashboard", to: "/admin/car/dashboard" };
export const CAR_AGENTS_MENU_ITEM = { label: "C.A.R. Agents", to: "/admin/car/agents" };

// A CAR admin's home page: CAR Dashboard replaces the old (Super Admin) Dashboard.
export const CAR_ADMIN_HOME = CAR_DASHBOARD_MENU_ITEM.to;

// Customer Service Queue tabs and Users quick filters a CAR admin does not see.
export const CAR_ADMIN_HIDDEN_CSQ_TABS = ["internal", "ai_agents", "mls_scans"];
export const CAR_ADMIN_HIDDEN_USER_FILTERS = ["professionals", "service_providers"];

// CAR admins start with the CAR screens; the old Dashboard is not in their menu.
export function countyRecorderMenuItems(isCarPartner = isCarPartnerSession()) {
  if (!isCarPartner) {
    return COUNTY_RECORDER_MENU;
  }
  return [
    CAR_DASHBOARD_MENU_ITEM,
    CAR_AGENTS_MENU_ITEM,
    ...COUNTY_RECORDER_MENU.slice(1),
  ];
}

// The CAR agent screens are for EP Super Admins and CAR admins, not county recorders.
export function canOpenCarAdminScreens(roles, isCarPartner = isCarPartnerSession()) {
  const list = storedRoleList(roles);
  if (list.includes(ROLES.admin)) {
    return true;
  }
  return list.includes(ROLES.countyRecorder) && isCarPartner;
}

// The profile is the source of truth; the stored flag is only written after it
// loads, and login clears it when the login response leaves the scope out.
export function carPartnerFromProfile(userProfileDetails) {
  if (userProfileDetails?.success) {
    return userProfileDetails?.data?.admin_partner_scope === "car";
  }
  return isCarPartnerSession();
}

// For pages: re-renders when the profile loads, so a CAR admin is recognised
// even before the stored flag is written (same check as the sidebar).
export function useIsCarPartner() {
  const userProfileDetails = useSelector(
    (state) => state?.userProfile?.userProfileDetails,
  );
  return carPartnerFromProfile(userProfileDetails);
}

export function shouldRequestRecorderDashboard() {
  return isCountyRecorderSession() && !isCarPartnerSession();
}

export function resolveAdminHome(shouldRequest, responseSucceeded) {
  return shouldRequest && responseSucceeded ? "recorder" : "admin";
}

export function rememberAdminPartnerScope(data) {
  if (data?.admin_partner_scope === "car") {
    localStorage.setItem("adminPartnerScope", "car");
    return;
  }
  localStorage.removeItem("adminPartnerScope");
}

export function countyAdminCreateUrl(values) {
  if (values?.partner_scope === "car" || values?.access === "car") {
    return "admin/county/partner/create/user";
  }
  const county = values?.county_ids?.[0] || values?.county;
  return `admin/county/${county}/create/user`;
}

export function countyAdminSubmitValues(values) {
  if (values?.access === "car") {
    const next = { ...values, partner_scope: "car", county: "" };
    delete next.county_ids;
    return next;
  }
  return values;
}

// Property workspace tabs whose actions a county recorder cannot run.
export const COUNTY_RECORDER_HIDDEN_PROPERTY_TABS = ["sign"];

export function usersToolbarActionsForRecorder(isRecorder, actions) {
  if (!isRecorder) {
    return actions;
  }

  return {
    ...actions,
    onAddProfessional: undefined,
    onAddServiceProvider: undefined,
    onExportCsv: undefined,
  };
}
